import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BriefcaseBusinessIcon,
  ClockIcons,
  MapPinIcons,
  OfficeIcons,
} from '../../../../icons/icons';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { Dialog } from '../../../../ui';
import {
  ApplySection,
  DocumentChecklist,
  JobInfoCards,
  JobPostSkeleton,
  JobUnavailable,
  QualifyingQuestions,
  ShareSection,
  VideoSection,
} from './components';
import { useJobPostDetails } from './Hooks';


const LANG_NAMES: Record<string, string> = {
  en: 'English', id: 'Indonesian', ms: 'Malay', zh: '中文 (简)', 'zh-TW': '中文 (繁)',
  ja: '日本語', ko: '한국어', th: 'ภาษาไทย', vi: 'Tiếng Việt',
  ar: 'عربي', fr: 'Français', de: 'Deutsch', es: 'Español', pt: 'Português',
  si: 'Sinhala', ta: 'Tamil', ru: 'Russian',
};
const langName = (code: string) => LANG_NAMES[code] ?? code.toUpperCase();

export const JobPostDetails = () => {
  const { t } = useTranslation(NS.jobPortal);
  const [isLogoBroken, setIsLogoBroken] = useState(false);

  const {
    jobPostData,
    loading,
    isDialogOpen,
    isModalOpen,
    selectedFiles,
    url,
    isPlaying,
    videoRef,
    handlePlayPause,
    openDialog,
    handleApply,
    handleFileSelect,
    handleFileRemoveAtIndex,
    handleBatchFileSelect,
    handleSubmit,
    handleCloseModal,
    copyToClipboard,
    shareOnLinkedIn,
    shareOnWhatsApp,
    shareOnFacebook,
    shareOnX,
    formatUnixTimestamp,
    calculateDaysLeft,
    companyLogo,
    companyName,
    DIALOG_TYPE,
    mailtoUrl,
    setIsDialogOpen,
    isQualifyingModalOpen,
    setIsQualifyingModalOpen,
    isQualifyingCancelDialogOpen,
    setIsQualifyingCancelDialogOpen,
    currentQualifyingStep,
    setCurrentQualifyingStep,
    qualifyingQuestions,
    qualifyingAnswers,
    setQualifyingAnswers,
    handleQualifyingComplete,
    isFinalQualifyingStep,
    canSubmit,
    ccaEnabled,
    contentLang,
    setContentLang,
  } = useJobPostDetails();

  const keyInfo = [
    {
      key: 'department',
      label: t(JOB_PORTAL_KEYS.details.department),
      value: jobPostData?.department || t(JOB_PORTAL_KEYS.details.notSpecified),
      icon: OfficeIcons,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      key: 'location',
      label: t(JOB_PORTAL_KEYS.details.location),
      value: jobPostData?.location || t(JOB_PORTAL_KEYS.details.notSpecified),
      icon: MapPinIcons,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      key: 'workType',
      label: t(JOB_PORTAL_KEYS.details.workType),
      value: jobPostData?.workType || t(JOB_PORTAL_KEYS.details.notSpecified),
      icon: BriefcaseBusinessIcon,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      key: 'timeline',
      label: t(JOB_PORTAL_KEYS.details.daysLeft),
      value: `${calculateDaysLeft()} - ${formatUnixTimestamp(
        jobPostData?.startDate ?? 0,
      )} - ${formatUnixTimestamp(jobPostData?.endDate ?? 0)}`,
      icon: ClockIcons,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <div className='min-h-screen bg-slate-50'>
      { /* Job Header */ }
      <div className='border-b border-slate-200 bg-white shadow-sm'>
        <div className='mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
            <div className='flex min-w-0 items-center gap-3'>
              <div className='flex h-12 min-w-12 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 shadow-sm'>
                { companyLogo && !isLogoBroken ? (
                  <img
                      src={companyLogo}
                      alt='Company Logo'
                      className='w-auto h-12 object-contain'
                      onError={() => setIsLogoBroken(true)}
                  />
                ) : (
                  <BriefcaseBusinessIcon className='h-5 w-5 text-slate-600' />
                ) }
              </div>
              <div className='min-w-0'>
                <div className='mb-1 flex flex-wrap items-center gap-2'>
                  <span className='inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700'>
                    <span className='mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500' />
                    { t(JOB_PORTAL_KEYS.details.hiring) }
                  </span>
                </div>
                <h1 className='truncate text-xl font-extrabold text-slate-950'>
                  { jobPostData?.jobTitle }
                </h1>
              </div>
            </div>

            <div className='flex flex-wrap items-center gap-2'>

              { jobPostData?.availableLanguages && jobPostData.availableLanguages.length >= 1 && (
                <div className='flex items-center gap-1.5'>
                  <label htmlFor='content-lang' className='text-xs font-medium text-slate-500 whitespace-nowrap'>
                    View in:
                  </label>
                  <select
                      id='content-lang'
                      value={contentLang}
                      onChange={(e) => setContentLang(e.target.value)}
                      className='rounded-md border border-slate-200 bg-white py-1 pl-2 pr-6 text-xs font-semibold text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500'
                  >
                    <option value='en'>{ langName('en') }</option>
                    { jobPostData.availableLanguages
                        .filter((l) => l !== 'en')
                        .map((lang) => (
                          <option key={lang} value={lang}>{ langName(lang) }</option>
                        )) }
                  </select>
                </div>
              ) }

              <div className='flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm'>
                <ClockIcons className='h-4 w-4 text-emerald-600 shrink-0' />
                <div className='whitespace-nowrap'>
                  <span className='font-bold text-emerald-800'>
                    { Math.max(
                      0,
                      Math.ceil(
                        (new Date((jobPostData?.endDate ?? 0) * 1000).getTime() -
                          Date.now()) /
                          (1000 * 60 * 60 * 24),
                      ),
                    ) }{ ' ' }
                    days left
                  </span>
                  <span className='ml-1 text-emerald-700'>to apply</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      { loading ? (
        <JobPostSkeleton />
      ) : jobPostData && jobPostData.active ? (
        <div className='mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]'>
            { /* Main Content */ }
            <div className='space-y-4'>
              { /* Job Info Cards */ }
              <JobInfoCards keyInfo={keyInfo} />

              { /* Job Description */ }
              <div className='rounded-lg border border-slate-200 bg-white p-5 shadow-sm'>
                <h2 className='mb-4 text-xl font-extrabold text-slate-900'>
                  { t(JOB_PORTAL_KEYS.details.jobDescription) }
                </h2>
                <div
                    className='prose prose-sm max-w-none text-slate-700
                    [&_h1]:mb-3 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-extrabold [&_h1]:text-slate-900
                    [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900
                    [&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-slate-800
                    [&_p]:mb-3 [&_p]:leading-6
                    [&_ul]:mb-3 [&_ul]:mt-1 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5
                    [&_li]:leading-6
                    [&_strong]:font-bold [&_strong]:text-slate-900'
                    dangerouslySetInnerHTML={{
                      __html: (jobPostData as any)?.htmlTemplate ?? '',
                    }}
                />
              </div>
            </div>

            { /* Sidebar */ }
            <div className='space-y-4'>
              <div className='sticky top-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm'>
                <VideoSection
                    handlePlayPause={handlePlayPause}
                    isPlaying={isPlaying}
                    jobPostData={jobPostData}
                    url={url}
                    videoRef={videoRef}
                />
                <ApplySection
                    onApplyClick={() => openDialog(DIALOG_TYPE.INFO)}
                />
                <ShareSection
                    shareOnX={shareOnX}
                    shareOnWhatsApp={shareOnWhatsApp}
                    shareOnLinkedIn={shareOnLinkedIn}
                    copyToClipboard={copyToClipboard}
                    mailtoUrl={mailtoUrl}
                    jobPostData={jobPostData}
                    shareOnFacebook={shareOnFacebook}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <JobUnavailable jobPostData={jobPostData} />
      ) }

      { /* Privacy Notice dialog */ }
      <Dialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          title={t(JOB_PORTAL_KEYS.details.privacyNotice)}
          dialogType={DIALOG_TYPE.SUCCESS}
          handleApply={handleApply}
          justify='center'
          confirmButtonLable={t(JOB_PORTAL_KEYS.details.acceptAll)}
          cancelButtonLable={t(JOB_PORTAL_KEYS.details.declineAll)}
      >
        <div className='space-y-6'>
          <div className='space-y-4'>
            <p className='text-slate-600 text-[13px] leading-relaxed'>
              { t(JOB_PORTAL_KEYS.privacy.beforeLink, { companyName: companyName || 'our platform' }) }{ ' ' }
              <a
                  className='text-emerald-600 font-bold hover:underline'
                  href='https://www.hiacuity.com/privacy-policy'
                  target='_blank'
                  rel='noreferrer'
              >
                { t(JOB_PORTAL_KEYS.privacy.policyLink) }
              </a>{ ' ' }
              { t(JOB_PORTAL_KEYS.privacy.afterLink) }
            </p>
            <p className='text-slate-500 text-[12px] italic'>
              { t(JOB_PORTAL_KEYS.privacy.consentText, { companyName: companyName || 'our platform' }) }
            </p>
          </div>

          <div className='bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 space-y-3'>
            <div className='flex items-center gap-2'>
              <div className='w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-[10px] text-white font-bold'>
                i
              </div>
              <h4 className='text-xs font-black text-emerald-800 uppercase tracking-wider'>
                { t(JOB_PORTAL_KEYS.details.importantNotice) }
              </h4>
            </div>
            <div className='space-y-2'>
              <div className='flex gap-3'>
                <div className='w-1 h-auto bg-emerald-300 rounded-full' />
                <p className='text-xs text-emerald-700 leading-normal'>
                  <span className='font-bold'>{ t(JOB_PORTAL_KEYS.details.phase1) }</span>{ ' ' }
                  { t(JOB_PORTAL_KEYS.details.phase1Text) }
                </p>
              </div>
              <div className='flex gap-3'>
                <div className='w-1 h-auto bg-emerald-300 rounded-full opacity-50' />
                <p className='text-xs text-emerald-700 leading-normal'>
                  <span className='font-bold'>{ t(JOB_PORTAL_KEYS.details.phase2) }</span>{ ' ' }
                  { t(JOB_PORTAL_KEYS.details.phase2Text) }
                </p>
              </div>
              { ccaEnabled && (
                <div className='flex gap-3'>
                  <div className='w-1 h-auto bg-blue-300 rounded-full opacity-60' />
                  <p className='text-xs text-blue-700 leading-normal'>
                    <span className='font-bold'>
                      Phase 3 - Cognitive Assessment:
                    </span>{ ' ' }
                    Once you submit the Pre-Screening Questionnaire, we will
                    send you an email invitation to complete a{ ' ' }
                    <span className='font-semibold italic'>
                      Cognitive Assessment (CCA)
                    </span>
                    . Please check your inbox and spam folder, then finish the
                    assessment to complete your application.
                  </p>
                </div>
              ) }
            </div>
          </div>
        </div>
      </Dialog>

      { /* Qualifying Questions dialog */ }
      <Dialog
          isOpen={isQualifyingModalOpen}
          onClose={() => {
            if (currentQualifyingStep === 0) {
              setIsQualifyingCancelDialogOpen(true);
            } else {
              setCurrentQualifyingStep((prev) => prev - 1);
            }
          }}
          closeOnOutsideClick={false}
          title={t(JOB_PORTAL_KEYS.details.preQualFormTitle, { jobTitle: jobPostData?.jobTitle || '' })}
          dialogType={DIALOG_TYPE.SUCCESS}
          justify='center'
          confirmButtonLable={loading ? t(JOB_PORTAL_KEYS.details.submitting) : isFinalQualifyingStep ? t(JOB_PORTAL_KEYS.details.finish) : t(JOB_PORTAL_KEYS.details.next)}
          cancelButtonLable={currentQualifyingStep === 0 ? t(JOB_PORTAL_KEYS.details.cancel) : t(JOB_PORTAL_KEYS.details.back)}
          handleApply={handleQualifyingComplete}
          disabled={
            loading ||
            !qualifyingAnswers.find(
              (a) => a.questionId === qualifyingQuestions[currentQualifyingStep]?.questionId,
            ) ||
            qualifyingAnswers.find(
              (a) => a.questionId === qualifyingQuestions[currentQualifyingStep]?.questionId,
            )?.answer === ''
          }
          maxWidth='650px'
      >
        <div className='space-y-4'>
          <QualifyingQuestions
              questions={qualifyingQuestions}
              currentStep={currentQualifyingStep}
              onComplete={setQualifyingAnswers}
          />
        </div>
      </Dialog>

      { /* Cancel confirmation dialog */ }
      <Dialog
          title={t(JOB_PORTAL_KEYS.details.cancelConfirmTitle)}
          isOpen={isQualifyingCancelDialogOpen}
          onClose={() => setIsQualifyingCancelDialogOpen(false)}
          handleApply={() => {
            setIsQualifyingCancelDialogOpen(false);
            setIsQualifyingModalOpen(false);
          }}
          confirmButtonLable={t(JOB_PORTAL_KEYS.details.cancelConfirmYes)}
          cancelButtonLable={t(JOB_PORTAL_KEYS.details.cancelConfirmNo)}
          dialogType={DIALOG_TYPE.WARNING}
          justify='end'
      >
        <p className='text-gray-600 text-sm'>
          { t(JOB_PORTAL_KEYS.details.cancelConfirmMessage) }
        </p>
      </Dialog>

      { /* Document upload dialog */ }
      <Dialog
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={t(JOB_PORTAL_KEYS.details.uploadDocuments)}
          dialogType={DIALOG_TYPE.SUCCESS}
          justify='center'
          confirmButtonLable={loading ? t(JOB_PORTAL_KEYS.details.submitting) : t(JOB_PORTAL_KEYS.details.submitDocuments)}
          cancelButtonLable={t(JOB_PORTAL_KEYS.details.cancel)}
          handleApply={handleSubmit}
          disabled={!canSubmit}
          maxWidth='90vw'
          noScroll
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-600 mb-4'>
            { t(JOB_PORTAL_KEYS.details.uploadDocumentsMessage) }
          </p>
          <DocumentChecklist
              documentRequirements={jobPostData?.documentRequirements}
              configurations={jobPostData?.documentConfigurations}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
              onBatchFileSelect={handleBatchFileSelect}
              onFileRemoveAtIndex={handleFileRemoveAtIndex}
              disabled={loading}
          />
        </div>
      </Dialog>
    </div>
  );
};
